import { z } from 'zod';
import { defineStateMachine } from '../common/state-machine.js';
/** Roles de agente y subagente (`agent_configs.role`). */
export const AGENT_ROLES = [
    'ORIGINATION_ADVISOR',
    'PURCHASE_ASSISTANT',
    'ANALYST_COPILOT',
    'DOCUMENT_READER',
    'CASH_FLOW_ANALYST',
    'DECISION_MEMO',
    'MEMORY_EXTRACTOR',
    'SUMMARIZER',
    'INPUT_GUARD',
    'EVAL_JUDGE',
];
export const AgentRoleSchema = z.enum(AGENT_ROLES).meta({ id: 'AgentRole' });
/** Agentes conversacionales expuestos en la API pública (`agent: ADVISOR|PURCHASES`). */
export const PublicAgentSchema = z.enum(['ADVISOR', 'PURCHASES']).meta({ id: 'PublicAgent' });
export const PUBLIC_AGENT_ROLE = {
    ADVISOR: 'ORIGINATION_ADVISOR',
    PURCHASES: 'PURCHASE_ASSISTANT',
};
export const AgentChannelSchema = z
    .enum(['WEB', 'WIDGET', 'VOICE', 'CONSOLE'])
    .meta({ id: 'AgentChannel' });
/** `AgentSession` — §10. `TAKEN_OVER`: el agente no genera respuestas; el analista escribe turnos `human_agent`. */
export const AgentSessionMachine = defineStateMachine({
    name: 'AgentSession',
    states: ['ACTIVE', 'WAITING_USER', 'WAITING_HITL', 'TAKEN_OVER', 'INTERRUPTED', 'CLOSED'],
    initial: ['ACTIVE'],
    terminal: ['CLOSED'],
    invalidTransitionCode: 'PZ-AGT-409',
    transitions: {
        ACTIVE: ['WAITING_USER', 'WAITING_HITL', 'TAKEN_OVER', 'INTERRUPTED'],
        WAITING_USER: ['ACTIVE', 'TAKEN_OVER', 'CLOSED'],
        WAITING_HITL: ['ACTIVE'],
        TAKEN_OVER: ['ACTIVE'],
        INTERRUPTED: ['ACTIVE', 'CLOSED'],
        CLOSED: [],
    },
});
export const AgentSessionStatusSchema = AgentSessionMachine.schema.meta({
    id: 'AgentSessionStatus',
});
export const AGENT_SESSION_IDLE_CLOSE_SECONDS = 30 * 60;
export const TurnRoleSchema = z.enum(['user', 'assistant', 'system_note', 'human_agent']);
export const InputModeSchema = z.enum(['TEXT', 'VOICE', 'FILE']);
/** `HumanTask` (HITL) — §9. `due_at` vencido → `overdue = true` (no cambia de estado). */
export const HumanTaskMachine = defineStateMachine({
    name: 'HumanTask',
    states: ['OPEN', 'ASSIGNED', 'IN_PROGRESS', 'RESOLVED', 'CANCELLED'],
    initial: ['OPEN'],
    terminal: ['RESOLVED', 'CANCELLED'],
    transitions: {
        OPEN: ['ASSIGNED', 'CANCELLED'],
        // Reasignar cambia `assigned_to` sin cambiar de estado.
        ASSIGNED: ['IN_PROGRESS', 'CANCELLED'],
        IN_PROGRESS: ['RESOLVED'],
        RESOLVED: [],
        CANCELLED: [],
    },
});
export const HumanTaskStatusSchema = HumanTaskMachine.schema.meta({ id: 'HumanTaskStatus' });
export const HumanTaskTypeSchema = z
    .enum([
    'CREDIT_DECISION',
    'DOCUMENT_VALIDATION',
    'SESSION_TAKEOVER',
    'DISPUTE_DECISION',
    'EXCEPTION_REVIEW',
    'DISPUTE_OVERDUE',
    /** Intención en PENDING_CONFIRMATION > 24 h (WS-PAY, `payments`). */
    'STUCK_PENDING',
])
    .meta({ id: 'HumanTaskType' });
export const HumanTaskPrioritySchema = z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']);
/** Herramientas: clase de efecto. Ninguna mueve dinero ni decide crédito. */
export const ToolEffectSchema = z
    .enum(['read', 'draft', 'write-internal'])
    .meta({ id: 'ToolEffect' });
export const ToolInvocationStatusSchema = z.enum(['OK', 'ERROR', 'DENIED', 'HITL_REQUIRED']);
/** Guardas. */
export const GuardStageSchema = z.enum(['PRE_LLM', 'POST_LLM', 'PRE_TOOL', 'POST_TOOL']);
export const GuardNameSchema = z
    .enum([
    'NUMBERS_GROUNDED',
    'LOOP_DETECTOR',
    'PII_REDACTION',
    'INJECTION_SCAN',
    'PHASE_TOOL_POLICY',
    'OUTPUT_SCHEMA',
    'TONE',
    'BUDGET',
    'TOPIC_SCOPE',
])
    .meta({ id: 'GuardName' });
export const GuardOutcomeSchema = z.enum(['PASS', 'MODIFIED', 'BLOCKED']);
/** Acción reportada en `agent.guardrail.triggered`. */
export const GuardActionSchema = z.enum(['BLOCK', 'REDACT', 'ESCALATE']);
/** Memoria del cliente. Un solo `VALIDATED` vigente por (customer, key). */
export const MemoryFactStatusSchema = z
    .enum(['PROPOSED', 'VALIDATED', 'REJECTED', 'SUPERSEDED'])
    .meta({ id: 'MemoryFactStatus' });
export const MemorySourceTypeSchema = z.enum(['USER_STATEMENT', 'DOCUMENT', 'TOOL', 'ANALYST']);
export const PublishStatusSchema = z.enum(['DRAFT', 'PUBLISHED']);
//# sourceMappingURL=states.js.map